import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Package, DollarSign, ShoppingBag, Plus, Edit, Trash2, LogOut, AlertCircle, TrendingUp, Truck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import NotificationBell from "../components/NotificationBell";
import ImageUpload from "../components/ImageUpload";
import Navbar from "../components/Navbar";

const VendorDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [productDialog, setProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [productForm, setProductForm] = useState({
    name: "",
    description: "",
    price: "",
    category: "furniture",
    images: "",
    stock: "",
    tags: "",
    eco_sustainable: false
  });

  useEffect(() => {
    fetchDashboard();
    fetchProducts();
    fetchPayoutRequests();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/vendor/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/vendor/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error("Failed to fetch products");
    }
  };

  const fetchPayoutRequests = async () => {
    try {
      const response = await axios.get(`${API}/vendor/payout-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayoutRequests(response.data.requests);
    } catch (error) {
      console.error("Failed to fetch payout requests");
    }
  };

  const handleRequestPayout = async () => {
    const amount = parseFloat(payoutAmount);
    if (!amount || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    setRequestingPayout(true);
    try {
      await axios.post(
        `${API}/vendor/payout-request`,
        { amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Payout request submitted!");
      setPayoutAmount("");
      fetchPayoutRequests();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request payout");
    } finally {
      setRequestingPayout(false);
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${API}/orders/${orderId}/tracking`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Order status updated to ${newStatus}`);
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to update order status");
    }
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...productForm,
        price: parseFloat(productForm.price),
        stock: parseInt(productForm.stock),
        images: productForm.images.split(",").map(s => s.trim()).filter(Boolean),
        tags: productForm.tags.split(",").map(s => s.trim()).filter(Boolean)
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Product updated!");
      } else {
        await axios.post(`${API}/products`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Product created!");
      }

      setProductDialog(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    
    try {
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Product deleted!");
      fetchProducts();
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const resetForm = () => {
    setProductForm({
      name: "",
      description: "",
      price: "",
      category: "furniture",
      images: "",
      stock: "",
      tags: "",
      eco_sustainable: false
    });
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      images: product.images?.join(", ") || "",
      stock: product.stock.toString(),
      tags: product.tags?.join(", ") || "",
      eco_sustainable: product.eco_sustainable
    });
    setProductDialog(true);
  };

  const categories = [
    { value: "furniture", label: "Furniture" },
    { value: "interior-decor", label: "Interior Decor" },
    { value: "eco-sustainable", label: "Eco Sustainable" },
    { value: "handmade-crafts", label: "Handmade Crafts" },
    { value: "artwork", label: "Artwork" },
    { value: "finishing-materials", label: "Finishing Materials" },
    { value: "building-materials", label: "Building Materials" }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      {/* Navbar */}
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Pending Approval Warning */}
        {dashboard?.vendor_status === "pending" && (
          <div className="bg-[#D4A373]/10 border border-[#D4A373] p-4 mb-8 flex items-center gap-3" data-testid="pending-warning">
            <AlertCircle className="text-[#D4A373]" size={24} />
            <div>
              <p className="font-medium text-[#1A1B1A]">Account Pending Approval</p>
              <p className="text-sm text-[#5C5D5C]">Your vendor account is under review. You can add products but they won't be visible until approved.</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-['Cormorant_Garamond'] text-4xl font-light text-[#1A1B1A]">
              Vendor Dashboard
            </h1>
            <p className="text-[#5C5D5C] mt-1">{user?.name}</p>
          </div>
          <Dialog open={productDialog} onOpenChange={setProductDialog}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => { resetForm(); setEditingProduct(null); }}
                className="bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none"
                data-testid="add-product-btn"
              >
                <Plus size={18} className="mr-2" /> Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Cormorant_Garamond'] text-2xl">
                  {editingProduct ? "Edit Product" : "Add New Product"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmitProduct} className="space-y-4 mt-4">
                <div>
                  <label className="text-sm font-medium text-[#1A1B1A] mb-2 block">Product Name</label>
                  <Input
                    value={productForm.name}
                    onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                    className="rounded-none border-[#EAE6DF]"
                    required
                    data-testid="product-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A1B1A] mb-2 block">Description</label>
                  <Textarea
                    value={productForm.description}
                    onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                    className="rounded-none border-[#EAE6DF] min-h-[100px]"
                    required
                    data-testid="product-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-[#1A1B1A] mb-2 block">Price ($)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={productForm.price}
                      onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                      className="rounded-none border-[#EAE6DF]"
                      required
                      data-testid="product-price-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#1A1B1A] mb-2 block">Stock</label>
                    <Input
                      type="number"
                      value={productForm.stock}
                      onChange={(e) => setProductForm({...productForm, stock: e.target.value})}
                      className="rounded-none border-[#EAE6DF]"
                      required
                      data-testid="product-stock-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A1B1A] mb-2 block">Category</label>
                  <Select value={productForm.category} onValueChange={(v) => setProductForm({...productForm, category: v})}>
                    <SelectTrigger className="rounded-none border-[#EAE6DF]" data-testid="product-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A1B1A] mb-2 block">Product Images</label>
                  <ImageUpload
                    existingImages={productForm.images ? productForm.images.split(",").map(s => s.trim()).filter(Boolean) : []}
                    onImageUploaded={(urls) => setProductForm({...productForm, images: urls.join(", ")})}
                  />
                  <Input
                    value={productForm.images}
                    onChange={(e) => setProductForm({...productForm, images: e.target.value})}
                    className="rounded-none border-[#EAE6DF] mt-2"
                    placeholder="Or paste image URLs (comma separated)"
                    data-testid="product-images-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A1B1A] mb-2 block">Tags (comma separated)</label>
                  <Input
                    value={productForm.tags}
                    onChange={(e) => setProductForm({...productForm, tags: e.target.value})}
                    className="rounded-none border-[#EAE6DF]"
                    placeholder="modern, wooden, handmade"
                    data-testid="product-tags-input"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="eco" 
                    checked={productForm.eco_sustainable}
                    onCheckedChange={(c) => setProductForm({...productForm, eco_sustainable: c})}
                    data-testid="product-eco-checkbox"
                  />
                  <label htmlFor="eco" className="text-sm font-medium text-[#1A1B1A]">
                    Eco Sustainable Product
                  </label>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none"
                  data-testid="save-product-btn"
                >
                  {editingProduct ? "Update Product" : "Create Product"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-[#EAE6DF]">
          {["overview", "products", "orders", "payouts"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-4 px-2 font-medium transition-colors ${
                activeTab === tab 
                  ? "text-[#20C997] border-b-2 border-[#20C997]" 
                  : "text-[#5C5D5C] hover:text-[#1A1B1A]"
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="dashboard-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#5C5D5C] text-sm">Total Products</p>
                    <p className="text-3xl font-light text-[#1A1B1A] mt-1" data-testid="products-count">
                      {dashboard?.products_count || 0}
                    </p>
                  </div>
                  <Package size={24} className="text-[#20C997]" />
                </div>
              </div>

              <div className="dashboard-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#5C5D5C] text-sm">Total Orders</p>
                    <p className="text-3xl font-light text-[#1A1B1A] mt-1" data-testid="orders-count">
                      {dashboard?.total_orders || 0}
                    </p>
                  </div>
                  <ShoppingBag size={24} className="text-[#F06570]" />
                </div>
              </div>

              <div className="dashboard-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#5C5D5C] text-sm">Total Revenue</p>
                    <p className="text-3xl font-light text-[#1A1B1A] mt-1" data-testid="total-revenue">
                      £{dashboard?.total_revenue?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  <TrendingUp size={24} className="text-[#3E6656]" />
                </div>
              </div>

              <div className="dashboard-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#5C5D5C] text-sm">Pending Payout</p>
                    <p className="text-3xl font-light text-[#1A1B1A] mt-1" data-testid="pending-payout">
                      £{dashboard?.pending_payout?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  <DollarSign size={24} className="text-[#D4A373]" />
                </div>
              </div>
            </div>

            <div className="dashboard-card">
              <h3 className="font-medium text-[#1A1B1A] mb-4">Commission Rate</h3>
              <p className="text-2xl font-light text-[#20C997]">
                {((dashboard?.commission_rate || 0.20) * 100).toFixed(0)}%
              </p>
              <p className="text-sm text-[#5C5D5C] mt-2">
                {dashboard?.commission_rate === 0.15 
                  ? "Congratulations! You qualify for our high-volume seller discount."
                  : "Reach £10,000 in sales to qualify for 15% commission rate."}
              </p>
            </div>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === "products" && (
          <div className="space-y-4" data-testid="products-list">
            {products.length === 0 ? (
              <div className="dashboard-card text-center py-12">
                <Package size={48} className="mx-auto text-[#8E8F8E] mb-4" />
                <p className="text-[#5C5D5C]">No products yet</p>
                <Button 
                  onClick={() => setProductDialog(true)}
                  className="mt-4 bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none"
                >
                  Add Your First Product
                </Button>
              </div>
            ) : (
              products.map((product, index) => (
                <div key={product.id} className="dashboard-card flex gap-4" data-testid={`product-${index}`}>
                  <div className="w-20 h-20 bg-[#EFECE5] overflow-hidden flex-shrink-0">
                    <img 
                      src={product.images?.[0] || "https://via.placeholder.com/100"} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <h3 className="font-medium text-[#1A1B1A]">{product.name}</h3>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => openEditDialog(product)}
                          className="p-2 text-[#5C5D5C] hover:text-[#20C997]"
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          onClick={() => handleDeleteProduct(product.id)}
                          className="p-2 text-[#5C5D5C] hover:text-[#9E4747]"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-[#5C5D5C] mt-1">{product.category}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="font-medium text-[#1A1B1A]">£{product.price}</span>
                      <span className="text-sm text-[#5C5D5C]">{product.stock} in stock</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === "orders" && (
          <div className="space-y-4" data-testid="vendor-orders-list">
            {dashboard?.recent_orders?.length === 0 ? (
              <div className="dashboard-card text-center py-12">
                <ShoppingBag size={48} className="mx-auto text-[#8E8F8E] mb-4" />
                <p className="text-[#5C5D5C]">No orders yet</p>
              </div>
            ) : (
              dashboard?.recent_orders?.map((order, index) => (
                <div key={order.id} className="dashboard-card" data-testid={`vendor-order-${index}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-[#1A1B1A]">Order #{order.id.slice(0, 8)}</p>
                      <p className="text-sm text-[#5C5D5C] mt-1">
                        {new Date(order.created_at).toLocaleDateString("en-GB")}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Select
                        value={order.status}
                        onValueChange={(v) => handleUpdateOrderStatus(order.id, v)}
                      >
                        <SelectTrigger className="w-40 rounded-none border-[#EAE6DF] text-sm" data-testid={`order-status-${index}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="processing">Processing</SelectItem>
                          <SelectItem value="shipped">Dispatched</SelectItem>
                          <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-lg font-medium text-[#1A1B1A]">£{order.total?.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Payouts Tab */}
        {activeTab === "payouts" && (
          <div className="space-y-6">
            {/* Request Payout */}
            <div className="dashboard-card" data-testid="payout-request-section">
              <h3 className="font-medium text-[#1A1B1A] mb-4">Request Payout</h3>
              <p className="text-sm text-[#5C5D5C] mb-4">
                Available balance: <span className="font-medium text-[#1A1B1A]">£{(dashboard?.pending_payout || 0).toFixed(2)}</span>
              </p>
              <div className="flex gap-3">
                <Input
                  type="number"
                  step="0.01"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  placeholder="Amount in £"
                  className="flex-1 rounded-none border-[#EAE6DF]"
                  data-testid="payout-amount-input"
                />
                <Button
                  onClick={handleRequestPayout}
                  disabled={requestingPayout || !payoutAmount}
                  className="bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none px-6"
                  data-testid="request-payout-btn"
                >
                  {requestingPayout ? "Requesting..." : "Request Payout"}
                </Button>
              </div>
              <p className="text-xs text-[#8E8F8E] mt-2">
                Payouts are reviewed and processed by admin within 3-5 business days.
              </p>
            </div>

            {/* Payout History */}
            <div data-testid="payout-history">
              <h3 className="font-medium text-[#1A1B1A] mb-4">Payout History</h3>
              {payoutRequests.length === 0 ? (
                <div className="dashboard-card text-center py-8">
                  <DollarSign size={48} className="mx-auto text-[#8E8F8E] mb-4" />
                  <p className="text-[#5C5D5C]">No payout requests yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payoutRequests.map((payout, index) => (
                    <div key={payout.id} className="dashboard-card flex items-center justify-between" data-testid={`payout-${index}`}>
                      <div>
                        <p className="font-medium text-[#1A1B1A]">£{payout.amount?.toFixed(2)}</p>
                        <p className="text-xs text-[#5C5D5C]">
                          Requested {new Date(payout.requested_at).toLocaleDateString("en-GB")}
                        </p>
                      </div>
                      <span className={`text-xs px-3 py-1 rounded-full ${
                        payout.status === "approved" ? "bg-[#3E6656]/10 text-[#3E6656]" :
                        payout.status === "rejected" ? "bg-[#9E4747]/10 text-[#9E4747]" :
                        "bg-[#D4A373]/10 text-[#D4A373]"
                      }`}>
                        {payout.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}}
      </div>
    </div>
  );
};

export default VendorDashboard;
